import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from '../common/AsyncState';
import useAppScrollLock from '../../hooks/useAppScrollLock';
import { isTypingTarget } from '../../utils/isTypingTarget';
import HtmlSlideRenderer from './slides/HtmlSlideRenderer';
import { usePresentationSlides } from './usePresentationSlides';
import './PresentationMode.css';

const isDecodedImage = (img) => Boolean(img && img.complete && img.naturalWidth > 0);

const PresentSlideImage = ({ fullUrl, src, thumbUrl }) => {
  const full = fullUrl || src;
  const thumb = thumbUrl && thumbUrl !== full ? thumbUrl : null;
  const [fullReady, setFullReady] = useState(false);
  const fullRef = useRef(null);

  const markFull = useCallback((img) => {
    fullRef.current = img;
    if (isDecodedImage(img)) setFullReady(true);
  }, []);

  useEffect(() => {
    if (isDecodedImage(fullRef.current)) {
      setFullReady(true);
      return undefined;
    }
    setFullReady(false);
    const timer = window.setInterval(() => {
      if (isDecodedImage(fullRef.current)) {
        setFullReady(true);
        window.clearInterval(timer);
      }
    }, 50);
    return () => window.clearInterval(timer);
  }, [full]);

  const displaySrc = thumb && !fullReady ? thumb : full;

  return (
    <>
      <img
        alt=""
        className="wqpresent-slide-img"
        data-testid="wqpresent-slide-img"
        draggable={false}
        src={displaySrc}
      />
      <img
        alt=""
        aria-hidden="true"
        className="wqpresent-preload"
        data-testid="wqpresent-slide-full"
        draggable={false}
        onError={() => setFullReady(true)}
        onLoad={(event) => markFull(event.currentTarget)}
        ref={markFull}
        src={full}
      />
      {thumb && !fullReady ? (
        <div aria-hidden="true" className="wqpresent-slide-spinner wqpresent-slide-spinner--corner" data-testid="wqpresent-slide-spinner-corner">
          <Spinner label="원본 불러오는 중" size={16} tone="inverse" />
        </div>
      ) : null}
      {!thumb && !fullReady ? (
        <div aria-hidden="true" className="wqpresent-slide-spinner" data-testid="wqpresent-slide-spinner">
          <Spinner label="슬라이드 불러오는 중" size={32} tone="inverse" />
        </div>
      ) : null}
    </>
  );
};

const SWIPE_THRESHOLD_PX = 50;
const CURSOR_HIDE_MS = 3000;

const requestOverlayFullscreen = async (element) => {
  if (!element) return false;
  try {
    if (element.requestFullscreen) {
      await element.requestFullscreen();
      return true;
    }
    if (element.webkitRequestFullscreen) {
      await element.webkitRequestFullscreen();
      return true;
    }
  } catch (_error) {
    return false;
  }
  return false;
};

const exitOverlayFullscreen = async () => {
  try {
    if (document.fullscreenElement && document.exitFullscreen) {
      await document.exitFullscreen();
    } else if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
      await document.webkitExitFullscreen();
    }
  } catch (_error) {
    // 폴백 오버레이는 그대로 닫는다
  }
};

const PresentationMode = ({
  initialIndex = 0,
  onClose,
  slides = [],
}) => {
  const overlayRef = useRef(null);
  const touchStartXRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [uiVisible, setUiVisible] = useState(true);
  const { current, goFirst, goLast, goNext, goPrev, index, next, total } = usePresentationSlides(
    slides,
    initialIndex,
  );
  useAppScrollLock();

  const revealUi = useCallback(() => {
    setUiVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setUiVisible(false), CURSOR_HIDE_MS);
  }, []);

  const handleClose = useCallback(() => {
    exitOverlayFullscreen();
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    const element = overlayRef.current;
    requestOverlayFullscreen(element);
    revealUi();
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      exitOverlayFullscreen();
    };
  }, [revealUi]);

  useEffect(() => {
    const handler = (event) => {
      if (event.defaultPrevented) return;
      if (isTypingTarget(event.target)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        goPrev();
      } else if (event.key === 'ArrowRight' || event.key === ' ' || event.key === 'PageDown') {
        event.preventDefault();
        goNext();
      } else if (event.key === 'Home') {
        event.preventDefault();
        goFirst();
      } else if (event.key === 'End') {
        event.preventDefault();
        goLast();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goFirst, goLast, goNext, goPrev, handleClose]);

  useEffect(() => {
    window.addEventListener('mousemove', revealUi);
    return () => window.removeEventListener('mousemove', revealUi);
  }, [revealUi]);

  const onTouchStart = (event) => {
    touchStartXRef.current = event.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (event) => {
    const startX = touchStartXRef.current;
    if (startX == null) return;
    const endX = event.changedTouches[0]?.clientX ?? startX;
    const delta = endX - startX;
    if (Math.abs(delta) > SWIPE_THRESHOLD_PX) {
      if (delta > 0) goPrev();
      else goNext();
    }
    touchStartXRef.current = null;
  };

  if (typeof document === 'undefined' || !current || total === 0) return null;

  return createPortal(
    <div
      aria-label="슬라이드 발표 모드"
      aria-modal="true"
      className={uiVisible ? 'wqpresent-overlay is-ui' : 'wqpresent-overlay'}
      data-testid="wqpresent"
      onMouseDown={revealUi}
      onTouchEnd={onTouchEnd}
      onTouchStart={onTouchStart}
      ref={overlayRef}
      role="dialog"
    >
      <div className="wqpresent-stage">
        <div className="wqpresent-slide" data-testid="wqpresent-slide">
          {current.type === 'image' ? (
            <PresentSlideImage fullUrl={current.fullUrl} src={current.src} thumbUrl={current.thumbUrl} />
          ) : (
            <div className="wqpresent-canvas" data-testid="wqpresent-slide-canvas">
              <HtmlSlideRenderer slide={current.slide} themeId={current.themeId} />
            </div>
          )}
        </div>
        {next?.type === 'image' && (next.fullUrl || next.src) ? (
          <img alt="" aria-hidden="true" className="wqpresent-preload" data-testid="wqpresent-preload" src={next.fullUrl || next.src} />
        ) : null}
        {next?.type === 'canvas' && next.slide ? (
          <div aria-hidden="true" className="wqpresent-preload" data-testid="wqpresent-preload">
            <HtmlSlideRenderer slide={next.slide} themeId={next.themeId} />
          </div>
        ) : null}
        {index > 0 && (
          <button
            aria-label="이전 슬라이드"
            className="wqpresent-zone wqpresent-zone--prev"
            onClick={goPrev}
            type="button"
          />
        )}
        {index < total - 1 && (
          <button
            aria-label="다음 슬라이드"
            className="wqpresent-zone wqpresent-zone--next"
            onClick={goNext}
            type="button"
          />
        )}
      </div>

      <div aria-live="polite" className="wqpresent-counter wqpresent-ui" data-testid="wqpresent-counter">
        {index + 1} / {total}
      </div>
      <button
        aria-label="닫기"
        className="wqpresent-close wqpresent-ui"
        data-testid="wqpresent-close"
        onClick={handleClose}
        type="button"
      >
        <svg fill="none" height="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="20">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>,
    document.body,
  );
};

export default PresentationMode;
